<script setup>
/**
 * AdminLevelsView
 * Admin page for managing Levels/XP configuration:
 * - Conversion rule (ILS to points)
 * - Levels CRUD (name, minPoints)
 */
import { ref, computed, onMounted } from 'vue'
import { subscribeLevelsConfig, saveLevelsConfig } from '../../../core/services/levels.js'
import { useAuthStore } from '../../../app/store/index.js'
import { useFirestoreSubscription } from '@core/composables/useFirestoreSubscription.js'

const { track } = useFirestoreSubscription()

// ============================================
// State
// ============================================
const authStore = useAuthStore()

const config = ref(null)
const isLoading = ref(true)
const error = ref(null)
const isSaving = ref(false)
const saveMessage = ref('')

// Conversion form
const conversionForm = ref({
  amount_ils: 1000,
  points: 1,
})

// Levels list
const levels = ref([])

// Modal state
const showModal = ref(false)
const modalMode = ref('add') // 'add' or 'edit'
const editingIndex = ref(-1)
const levelForm = ref({
  level: 1,
  name: '',
  minPoints: 0,
})
const modalError = ref('')

// Delete confirmation
const showDeleteConfirm = ref(false)
const deleteIndex = ref(-1)

// ============================================
// Subscription
// ============================================
onMounted(() => {
  track(
    subscribeLevelsConfig((data, err) => {
      if (err) {
        console.error('Levels config error:', err)
        error.value = err.message || 'שגיאה בטעינת הגדרות'
      } else if (data) {
        config.value = data
        // Initialize forms from config
        if (data.conversion) {
          conversionForm.value = { ...data.conversion }
        }
        if (data.levels) {
          levels.value = [...data.levels].sort((a, b) => a.minPoints - b.minPoints)
        }
        error.value = null
      }
      isLoading.value = false
    })
  )
})

// ============================================
// Computed
// ============================================
const sortedLevels = computed(() => {
  return [...levels.value].sort((a, b) => a.minPoints - b.minPoints)
})

const nextLevelNumber = computed(() => {
  if (levels.value.length === 0) return 1
  const maxLevel = Math.max(...levels.value.map((l) => l.level))
  return maxLevel + 1
})

// ============================================
// Conversion Actions
// ============================================
async function saveConversion() {
  if (conversionForm.value.amount_ils < 1) {
    saveMessage.value = 'סכום חייב להיות לפחות 1'
    return
  }
  if (conversionForm.value.points < 1) {
    saveMessage.value = 'נקודות חייבות להיות לפחות 1'
    return
  }

  isSaving.value = true
  saveMessage.value = ''

  try {
    await saveLevelsConfig(
      {
        conversion: { ...conversionForm.value },
        levels: levels.value,
      },
      authStore.userEmail || authStore.userId
    )

    saveMessage.value = 'נשמר בהצלחה'
    setTimeout(() => (saveMessage.value = ''), 3000)
  } catch (err) {
    console.error('Save conversion error:', err)
    saveMessage.value = 'שגיאה בשמירה: ' + err.message
  } finally {
    isSaving.value = false
  }
}

// ============================================
// Level CRUD Actions
// ============================================
function openAddModal() {
  modalMode.value = 'add'
  editingIndex.value = -1
  levelForm.value = {
    level: nextLevelNumber.value,
    name: '',
    minPoints: 0,
  }
  modalError.value = ''
  showModal.value = true
}

function openEditModal(index) {
  const level = sortedLevels.value[index]
  modalMode.value = 'edit'
  editingIndex.value = levels.value.findIndex((l) => l.level === level.level)
  levelForm.value = { ...level }
  modalError.value = ''
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  modalError.value = ''
}

async function saveLevel() {
  // Validation
  if (!levelForm.value.name.trim()) {
    modalError.value = 'שם רמה חובה'
    return
  }
  if (levelForm.value.level < 1) {
    modalError.value = 'מספר רמה חייב להיות לפחות 1'
    return
  }
  if (levelForm.value.minPoints < 0) {
    modalError.value = 'נקודות מינימום לא יכולות להיות שליליות'
    return
  }

  // Check for duplicate level number (except when editing same)
  const duplicateLevel = levels.value.find(
    (l, i) =>
      l.level === levelForm.value.level && (modalMode.value === 'add' || i !== editingIndex.value)
  )
  if (duplicateLevel) {
    modalError.value = 'מספר רמה כבר קיים'
    return
  }

  isSaving.value = true
  modalError.value = ''

  try {
    let updatedLevels
    if (modalMode.value === 'add') {
      updatedLevels = [...levels.value, { ...levelForm.value }]
    } else {
      updatedLevels = levels.value.map((l, i) =>
        i === editingIndex.value ? { ...levelForm.value } : l
      )
    }

    // Sort by minPoints
    updatedLevels.sort((a, b) => a.minPoints - b.minPoints)

    await saveLevelsConfig(
      {
        conversion: conversionForm.value,
        levels: updatedLevels,
      },
      authStore.userEmail || authStore.userId
    )

    closeModal()
  } catch (err) {
    console.error('Save level error:', err)
    modalError.value = 'שגיאה בשמירה: ' + err.message
  } finally {
    isSaving.value = false
  }
}

function confirmDelete(index) {
  const level = sortedLevels.value[index]

  // Cannot delete level 1 (minPoints 0)
  if (level.minPoints === 0 || level.level === 1) {
    error.value = 'לא ניתן למחוק את רמה 1 (רמת הבסיס)'
    setTimeout(() => (error.value = null), 3000)
    return
  }

  deleteIndex.value = levels.value.findIndex((l) => l.level === level.level)
  showDeleteConfirm.value = true
}

function cancelDelete() {
  showDeleteConfirm.value = false
  deleteIndex.value = -1
}

async function executeDelete() {
  if (deleteIndex.value < 0) return

  isSaving.value = true

  try {
    const updatedLevels = levels.value.filter((_, i) => i !== deleteIndex.value)

    await saveLevelsConfig(
      {
        conversion: conversionForm.value,
        levels: updatedLevels,
      },
      authStore.userEmail || authStore.userId
    )

    cancelDelete()
  } catch (err) {
    console.error('Delete level error:', err)
    error.value = 'שגיאה במחיקה: ' + err.message
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div class="admin-levels-page">
    <header class="page-header">
      <h1 class="page-title">ניהול רמות וניקוד</h1>
      <p class="page-subtitle">הגדרת כלל ההמרה ורמות המשתמשים</p>
    </header>

    <!-- Error Banner -->
    <div v-if="error" class="error-banner">
      {{ error }}
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <span>טוען הגדרות...</span>
    </div>

    <!-- Content -->
    <div v-else class="page-content">
      <!-- Conversion Rule Card -->
      <section class="config-card">
        <h2 class="card-title">כלל המרה</h2>
        <p class="card-description">קביעת היחס בין סכום הזמנה לנקודות</p>

        <div class="conversion-form">
          <!-- Desktop layout -->
          <div class="conversion-row desktop-only">
            <span class="conversion-label">כל</span>
            <input
              type="number"
              v-model.number="conversionForm.amount_ils"
              min="1"
              class="conversion-input"
            />
            <span class="conversion-label">ש"ח</span>
            <span class="conversion-arrow">=</span>
            <input
              type="number"
              v-model.number="conversionForm.points"
              min="1"
              class="conversion-input conversion-input-sm"
            />
            <span class="conversion-label">נקודות</span>
          </div>

          <!-- Mobile layout -->
          <div class="conversion-mobile mobile-only">
            <div class="conversion-group">
              <label class="conversion-group-label">סכום הזמנה (ש"ח)</label>
              <input
                type="number"
                v-model.number="conversionForm.amount_ils"
                min="1"
                class="conversion-input"
              />
            </div>
            <div class="conversion-equals">=</div>
            <div class="conversion-group">
              <label class="conversion-group-label">נקודות</label>
              <input
                type="number"
                v-model.number="conversionForm.points"
                min="1"
                class="conversion-input"
              />
            </div>
          </div>

          <div class="conversion-actions">
            <button
              class="btn btn-primary btn-full-mobile"
              :disabled="isSaving"
              @click="saveConversion"
            >
              {{ isSaving ? 'שומר...' : 'שמור כלל המרה' }}
            </button>
            <span
              v-if="saveMessage"
              class="save-message"
              :class="{ error: saveMessage.includes('שגיאה') }"
            >
              {{ saveMessage }}
            </span>
          </div>
        </div>
      </section>

      <!-- Levels CRUD Card -->
      <section class="config-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">רמות</h2>
            <p class="card-description">הגדרת רמות והנקודות הנדרשות</p>
          </div>
          <button class="btn btn-primary" @click="openAddModal">+ הוסף רמה</button>
        </div>

        <!-- Desktop Table -->
        <div class="levels-table-wrapper desktop-only">
          <table class="levels-table">
            <thead>
              <tr>
                <th>רמה</th>
                <th>שם</th>
                <th>נקודות מינימום</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(level, index) in sortedLevels" :key="level.level">
                <td class="level-number">{{ level.level }}</td>
                <td class="level-name">{{ level.name }}</td>
                <td class="level-points">{{ level.minPoints.toLocaleString() }}</td>
                <td class="level-actions">
                  <button class="btn-icon" title="עריכה" @click="openEditModal(index)">
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    class="btn-icon btn-icon-danger"
                    title="מחיקה"
                    :disabled="level.minPoints === 0 || level.level === 1"
                    @click="confirmDelete(index)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path
                        d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="sortedLevels.length === 0">
                <td colspan="4" class="empty-row">אין רמות מוגדרות</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Cards -->
        <div class="levels-cards mobile-only">
          <div v-if="sortedLevels.length === 0" class="empty-state">אין רמות מוגדרות</div>
          <div v-for="(level, index) in sortedLevels" :key="level.level" class="level-card">
            <div class="level-card-header">
              <span class="level-badge">רמה {{ level.level }}</span>
              <div class="level-card-actions">
                <button class="btn-icon" title="עריכה" @click="openEditModal(index)">
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  class="btn-icon btn-icon-danger"
                  title="מחיקה"
                  :disabled="level.minPoints === 0 || level.level === 1"
                  @click="confirmDelete(index)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path
                      d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div class="level-card-body">
              <div class="level-card-name">{{ level.name }}</div>
              <div class="level-card-points">
                <span class="points-label">נקודות מינימום:</span>
                <span class="points-value">{{ level.minPoints.toLocaleString() }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ modalMode === 'add' ? 'הוספת רמה' : 'עריכת רמה' }}</h3>
          <button class="modal-close" @click="closeModal">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="modalError" class="modal-error">{{ modalError }}</div>

          <div class="form-group">
            <label>מספר רמה</label>
            <input type="number" v-model.number="levelForm.level" min="1" class="form-input" />
          </div>

          <div class="form-group">
            <label>שם הרמה</label>
            <input
              type="text"
              v-model="levelForm.name"
              placeholder="לדוגמה: מתחיל, מתקדם, מקצוען"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label>נקודות מינימום</label>
            <input type="number" v-model.number="levelForm.minPoints" min="0" class="form-input" />
            <span class="form-hint">מספר הנקודות הנדרש להגיע לרמה זו</span>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModal">ביטול</button>
          <button class="btn btn-primary" :disabled="isSaving" @click="saveLevel">
            {{ isSaving ? 'שומר...' : 'שמור' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h3>אישור מחיקה</h3>
        </div>
        <div class="modal-body">
          <p>האם אתה בטוח שברצונך למחוק רמה זו?</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="cancelDelete">ביטול</button>
          <button class="btn btn-danger" :disabled="isSaving" @click="executeDelete">
            {{ isSaving ? 'מוחק...' : 'מחק' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ============================================
   Page Layout
   ============================================ */
.admin-levels-page {
  padding: var(--spacing-lg);
  max-width: 900px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--spacing-xl);
}

.page-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.page-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

/* ============================================
   Error & Loading
   ============================================ */
.error-banner {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   Config Cards
   ============================================ */
.config-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-sm);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-lg);
}

.card-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.card-description {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* ============================================
   Conversion Form
   ============================================ */
.conversion-form {
  margin-top: var(--spacing-lg);
}

.conversion-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.conversion-label {
  font-size: var(--font-size-base);
  color: var(--text-secondary);
}

.conversion-input {
  width: 100px;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  text-align: center;
}

.conversion-input-sm {
  width: 100px;
}

.conversion-arrow {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  padding: 0 var(--spacing-sm);
}

.conversion-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.save-message {
  font-size: var(--font-size-sm);
  color: var(--color-success);
}

.save-message.error {
  color: var(--color-error);
}

/* ============================================
   Levels Table
   ============================================ */
.levels-table-wrapper {
  overflow-x: auto;
}

.levels-table {
  width: 100%;
  border-collapse: collapse;
}

.levels-table th,
.levels-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: right;
  border-bottom: 1px solid var(--border-color);
}

.levels-table th {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  text-transform: uppercase;
  background: var(--bg-secondary);
}

.level-number {
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.level-name {
  font-weight: var(--font-weight-medium);
}

.level-points {
  font-family: monospace;
}

.level-actions {
  display: flex;
  gap: var(--spacing-xs);
}

.empty-row {
  text-align: center;
  color: var(--text-muted);
  padding: var(--spacing-lg) !important;
}

/* ============================================
   Buttons
   ============================================ */
.btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-dark);
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.btn-danger {
  background: var(--color-error);
  color: white;
}

.btn-danger:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-icon:hover:not(:disabled) {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btn-icon-danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
}

/* ============================================
   Modal
   ============================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
}

.modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-xl);
}

.modal-sm {
  max-width: 320px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
}

.modal-header h3 {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--border-radius);
}

.modal-close:hover {
  background: var(--bg-secondary);
}

.modal-body {
  padding: var(--spacing-lg);
}

.modal-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
  font-size: var(--font-size-sm);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
}

/* ============================================
   Form
   ============================================ */
.form-group {
  margin-bottom: var(--spacing-md);
}

.form-group label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
}

.form-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-family: var(--font-family);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-hint {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: var(--spacing-xs);
}

/* ============================================
   Visibility Helpers
   ============================================ */
.mobile-only {
  display: none !important;
}

/* ============================================
   Mobile Cards for Levels
   ============================================ */
.levels-cards {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.level-card {
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
}

.level-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
}

.level-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark));
  color: white;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
}

.level-card-actions {
  display: flex;
  gap: var(--spacing-xs);
}

.level-card-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.level-card-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.level-card-points {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
}

.points-label {
  color: var(--text-muted);
}

.points-value {
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  font-family: monospace;
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

/* ============================================
   Mobile Conversion Form
   ============================================ */
.conversion-mobile {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.conversion-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.conversion-group-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.conversion-equals {
  text-align: center;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  padding: var(--spacing-xs) 0;
}

.btn-full-mobile {
  width: auto;
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .mobile-only.levels-cards {
    display: flex !important;
  }

  .mobile-only.conversion-mobile {
    display: flex !important;
  }

  .admin-levels-page {
    padding: var(--spacing-md);
  }

  .page-header {
    margin-bottom: var(--spacing-lg);
  }

  .page-title {
    font-size: var(--font-size-lg);
  }

  .config-card {
    padding: var(--spacing-md);
  }

  .card-header {
    flex-direction: column;
    gap: var(--spacing-md);
    align-items: stretch;
  }

  .card-header .btn {
    width: 100%;
    justify-content: center;
  }

  .conversion-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-full-mobile {
    width: 100%;
  }

  .save-message {
    text-align: center;
  }

  .modal {
    margin: var(--spacing-sm);
    max-height: calc(100vh - var(--spacing-lg));
    overflow-y: auto;
  }

  .modal-footer {
    flex-direction: column-reverse;
    gap: var(--spacing-sm);
  }

  .modal-footer .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
